<template>
  <div>
    <vx-card class="mb-5">
      <p class="mb-5">
        In the video below, The Australian Hairdressing Council provides some insight into what it's
        like to work in the hairdressing industry. Here, you’ll find some information on training
        pathways to become a hairdresser and some exciting avenues this type of work can lead to.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full mb-5">
          <responsive-iframe-youtube
            :src="intro_videos[1].video"
            :posterimg="intro_videos[1].image"
          />
        </div>
      </div>
    </vx-card>

    <vx-card title="Tools and Equipment Used in Hairdressing:">
      <p class="mb-5">
        As you work your way through your hairdressing experience, you will come across a range of
        tools and equipment used in this trade. Click on the image below to open a document that
        gives you a description of many of these tools. You can print the sheet out to use as a
        reference.
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col my-5">
          <a :href="'content/hairdressing/hairdressing_tools.pdf' | blob_url" target="_blank">
            <img
              src="@/assets/images/experiences/hairdressing/hairdressing_tools.png"
              alt="View the hairdressing tools guide (PDF, opens in a new tab)"
              class="mx-auto img-responsive"
            />
          </a>
        </div>
      </div>
    </vx-card>

    <vx-card title="Try Out Some Hairstyles:">
      <p class="mb-5">Use the hairstyling tool below to try out some different hairstyles.</p>
      <a
        href="https://www.hairtotry.com/tool"
        target="_blank"
        class="no-underline btn btn-secondary btn-size-medium includedIcon inline-block"
      >
        <download-icon />
        Open Hairstyling Tool
      </a>
    </vx-card>

    <vx-card title="Hairdressing Terminology:">
      <p class="mb-5">
        As a hairdresser there are a lot of terms you need to know. The video below goes through
        some of these for you.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full mb-5">
          <responsive-iframe-youtube
            :src="intro_videos[2].video"
            :posterimg="intro_videos[2].image"
          />
        </div>
      </div>
    </vx-card>

    <vx-card title="Explore Options, Stories and Opportunities:">
      <p class="mb-5">
        Hairdressing can be hard work, but there is creativity and glamour that comes from passion
        and self-expression.
      </p>
      <p class="mb-5">
        Here is one example of a very successful worldwide franchise with nearly 700 hair salons in
        30 countries across the globe!
      </p>
      <p class="mb-5">
        Franck Provost has grown significantly across Australia and has laid the foundations to
        become one of the leading high-end hairdressing franchises.
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full mb-5">
          <responsive-iframe-youtube
            :src="intro_videos[3].video"
            :posterimg="intro_videos[3].image"
          />
        </div>
      </div>
      <p class="mb-5">
        Brittany, Senior Stylist at Franck Provost Paris and active member of the Franck Provost
        Studio team, shares her highlights.
      </p>

      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full mb-5">
          <responsive-iframe-youtube
            :src="intro_videos[4].video"
            :posterimg="intro_videos[4].image"
          />
        </div>
      </div>

      <p class="mb-5">
        Keep in mind this is one story and that there are many options and paths to take when you
        become a qualified hairdresser. You may wish to:
      </p>

      <ul class="default-list">
        <li>
          <strong>See the world.</strong> You can take your talent and scissors wherever you travel.
        </li>
        <li>
          <strong>Have job security.</strong> Even during tough economic times people still get
          their hair cut.
        </li>
        <li>
          <strong>Work from home.</strong> You may prefer the flexibility, the one-on-one client
          interaction, and low overheads.
        </li>
        <li>
          <strong>Have variety and meet a range of different people.</strong> You will certainly
          develop your interpersonal skills.
        </li>
        <li>
          <strong>Expand your talents amongst the celebs.</strong> Hair Stylists can work within
          entertainment industries of all kinds. It can be very exciting, and an opportunity to do a
          range of very different types of creative works.
        </li>
        <li>
          <strong>Own your own Salon.</strong> Being a business owner involves another layer of
          learning and responsibility, but also allows you to run the show!
        </li>
      </ul>

      <p class="mb-5">Do your own research! Explore other stories and opportunities.</p>
    </vx-card>
  </div>
</template>

<script>
import ResponsiveIframeYoutube from '@/components/UI/ResponsiveIframeYoutube';
import store from '@/store/store';
import { DownloadIcon } from 'vue-feather-icons';

export default {
  components: { ResponsiveIframeYoutube, DownloadIcon },
  data() {
    return {
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/hairdressing/intro_1.jpg'),
          video: 'https://www.youtube.com/embed/UhhL4V5CgtE',
        },
        2: {
          image: require('@/assets/images/experiences/hairdressing/terminology.jpg'),
          video: 'https://www.youtube.com/embed/eKKqL3XxUns',
        },
        3: {
          image: require('@/assets/images/experiences/hairdressing/hair_franchise_splash.jpg'),
          video: 'https://www.youtube.com/embed/nj1uLQ-APcQ',
        },
        4: {
          image: require('@/assets/images/experiences/hairdressing/hair_story.jpg'),
          video: 'https://www.youtube.com/embed/BBYtjqJJ4Es',
        },
      },
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  provide: function () {
    return {
      closePopup: this.closePopup,
    };
  },
  methods: {
    showVideoFile(video) {
      store.dispatch('popup/showVideoPopup', video);
    },
    showVideoYt(url) {
      this.ytVideoUrl = url;
      this.showVideoPopupYt = true;
    },
    closePopup() {
      if (this.showVideoPopupFile) {
        this.videoURL = false;
        this.showVideoPopupFile = false;
      } else if (this.showVideoPopupYt) {
        this.ytVideoUrl = false;
        this.showVideoPopupYt = false;
      }
    },
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
